import { type PropsWithChildren, useMemo } from 'react'

import { WagmiProvider } from 'wagmi'

import type { ExtendedChain } from '@lifi/sdk'
import { createDefaultWagmiConfig, useSyncWagmiConfig } from '@lifi/wallet-management'

import { ChainId, isTestnet } from '@src/configs/chainInfo'
import { defaultCoinbaseConfig } from '@src/configs/wallet/coinbase'
import { defaultMetaMaskConfig } from '@src/configs/wallet/metaMask'
import { defaultWalletConnectConfig } from '@src/configs/wallet/walletConnect'
import { useChains } from '@src/hooks/useChains'
import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'
import { safeAbstractWalletConnector } from '@src/utils/safeAbstractWalletConnector'

const logger = createContextLogger('AbstractEVMProvider')

// Check if Abstract Connector should be disabled (for testnets)
const enableAbstractConnector = isFeatureEnabled('ABSTRACT_CONNECTOR')
logger.info('Abstract Wallet connector status:', { enableAbstractConnector })

// Create the Wagmi config with appropriate connectors
const { config, connectors } = createDefaultWagmiConfig({
    // Only include Abstract Wallet if not disabled, using our safe version that won't throw errors
    connectors: enableAbstractConnector ? [safeAbstractWalletConnector()] : [],
    coinbase: defaultCoinbaseConfig,
    walletConnect: defaultWalletConnectConfig,
    metaMask: defaultMetaMaskConfig,
    lazy: true,
})

logger.debug('Wallet provider configuration:', {
    abstractWalletConnectorAvailable: enableAbstractConnector,
})

// Config used internally by this provider
// No longer exported as we're using viem clients directly elsewhere

/**
 * AbstractEVMProvider - EVM provider implementation with Abstract wallet support
 * Used when ABSTRACT_CONNECTOR feature is enabled
 */
export const AbstractEVMProvider = ({ children }: PropsWithChildren) => {
    const { chains, isSuccess, isError, error } = useChains()

    // Check if we should filter out testnet chains for the connector
    const filteredChains = useMemo(() => {
        // When abstract connector is enabled, filter out testnet chains
        if (enableAbstractConnector && chains) {
            // Filter out testnet chains when using the config using the isTestnet helper function
            const mainnetChains = chains.filter(chain => {
                // Skip chains that are identified as testnets
                return !isTestnet(chain.id as ChainId)
            })
            logger.info(
                `(isFeatureEnabled('ABSTRACT_CONNECTOR' = ${enableAbstractConnector})\nFiltered out testnet chains for Abstract Connector:`,
                {
                    originalChainCount: chains.length,
                    filteredChainCount: mainnetChains.length,
                }
            )
            return mainnetChains
        }
        return chains
    }, [chains])

    if (isError) {
        logger.error('Failed to load chains:', error)
    }

    if (isSuccess) {
        logger.info('Chains loaded successfully:', chains)
        logger.info(
            `(isFeatureEnabled('ABSTRACT_CONNECTOR' = ${enableAbstractConnector})\nFiltered chains for Abstract Connector:`,
            filteredChains
        )
    }

    // Always call useSyncWagmiConfig, but with filtered chains if ABSTRACT_CONNECTOR is enabled
    // This ensures that we only use the Abstract connector with mainnet chains
    useSyncWagmiConfig(config, connectors, filteredChains as ExtendedChain[])

    return (
        <WagmiProvider config={config} reconnectOnMount={false}>
            {children}
        </WagmiProvider>
    )
}
